import { RealtimeClient } from '@supabase/realtime-js'
import { getLoggedinUser } from '../helpers/api_helper'
import { getRealtimeBaseUrl } from '../utils/realtime'
import { getRealtimeAccessToken, subscribeToTokenUpdates } from './realtimeAuth'

/**
 * Shared RealtimeClient singleton
 * All realtime services should use this shared client to avoid multiple WebSocket connections
 */
class SharedRealtimeClient {
  private client: RealtimeClient | null = null
  private connectionPromise: Promise<void> | null = null
  private tokenUnsubscribe: (() => void) | null = null
  private isConnecting: boolean = false

  constructor() {
    // Subscribe to token updates
    this.tokenUnsubscribe = subscribeToTokenUpdates((newToken) => {
      this.handleTokenUpdate(newToken)
    })
  }

  private getAnonKey(): string | null {
    return (
      import.meta.env.VITE_SUPABASE_REALTIME_ANON_KEY ??
      import.meta.env.VITE_SUPABASE_ANON_KEY ??
      null
    )
  }

  /**
   * Handle token update by updating the client's access token
   */
  private handleTokenUpdate(newToken: string): void {
    if (!this.client) {
      console.log('[SharedRealtimeClient] No client to update with new token')
      return
    }

    console.log('[SharedRealtimeClient] Updating client with new token')

    try {
      this.client.setAuth(newToken)
      console.log('[SharedRealtimeClient] Client token updated successfully')
    } catch (error) {
      console.error('[SharedRealtimeClient] Failed to update client token:', error)
    }
  }

  /**
   * Get or create the shared RealtimeClient instance
   * This method ensures only one WebSocket connection is created
   */
  async getClient(): Promise<RealtimeClient | null> {
    const auth = getLoggedinUser()
    const isAuthenticated = Boolean(auth?.token)

    // If client exists and is connected, return it
    if (this.client && this.client.isConnected()) {
      return this.client
    }

    // If client exists but not connected, wait for connection
    if (this.client && !this.client.isConnected()) {
      console.log('[SharedRealtimeClient] Client exists but not connected, waiting...')

      // If connection is already in progress, wait for it
      if (this.connectionPromise) {
        console.log('[SharedRealtimeClient] Connection already in progress, waiting...')
        await this.connectionPromise
        return this.client
      }

      // Start new connection attempt
      if (!this.isConnecting) {
        this.isConnecting = true
        // A client object can survive a dropped socket. Explicitly reconnect it;
        // waiting alone can never make isConnected() become true once the SDK has
        // exhausted/closed the previous connection.
        this.client.connect()
        this.connectionPromise = this.waitForConnection()
        try {
          await this.connectionPromise
        } finally {
          this.isConnecting = false
          this.connectionPromise = null
        }
      }

      return this.client
    }

    // Client doesn't exist, create it (authenticated user JWT if available, otherwise anon key for public channels)
    console.log('[SharedRealtimeClient] Creating new RealtimeClient')
    const initialToken = isAuthenticated
      ? await getRealtimeAccessToken()
      : this.getAnonKey()

    if (!initialToken) {
      console.warn(
        `[SharedRealtimeClient] No ${isAuthenticated ? 'user realtime token' : 'anon realtime key'} available`
      )
      return null
    }

    this.client = new RealtimeClient(getRealtimeBaseUrl(), {
      params: {
        apikey: initialToken,
        vsn: '1.0.0',
      },
      ...(isAuthenticated ? { accessToken: getRealtimeAccessToken } : {}),
    })

    // Connect and wait for connection to be established
    this.isConnecting = true
    this.connectionPromise = this.waitForConnection()
    this.client.connect()
    try {
      await this.connectionPromise
    } finally {
      this.isConnecting = false
      this.connectionPromise = null
    }

    return this.client
  }

  /**
   * Wait for the client connection to be established
   */
  private async waitForConnection(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.client) {
        resolve()
        return
      }

      const checkConnection = () => {
        if (!this.client) {
          resolve()
          return
        }

        if (this.client.isConnected()) {
          console.log('[SharedRealtimeClient] Connection established')
          resolve()
        } else {
          setTimeout(checkConnection, 100)
        }
      }

      setTimeout(checkConnection, 100)

      // Timeout after 5 seconds
      setTimeout(() => {
        if (!this.client?.isConnected()) {
          console.warn('[SharedRealtimeClient] Connection timeout after 5 seconds')
        }
        resolve()
      }, 5000)
    })
  }

  /**
   * Disconnect and cleanup the client
   */
  disconnect(): void {
    if (this.client) {
      this.client.disconnect()
      this.client = null
    }

    this.connectionPromise = null
    this.isConnecting = false

    if (this.tokenUnsubscribe) {
      this.tokenUnsubscribe()
      this.tokenUnsubscribe = null
    }
  }

  /**
   * Get the current client without creating one
   * Useful for checking if client exists
   */
  getCurrentClient(): RealtimeClient | null {
    return this.client
  }
}

// Export singleton instance
export const sharedRealtimeClient = new SharedRealtimeClient()
